/**
 * Internal notes and the history of the legajo.
 *
 * Notes are how a team hands work over — "llamé al cliente, manda el balance el viernes" — and are
 * deliberately not part of the report. The history below them is the answer to "why does this
 * legajo look like this?", which is the question a reviewer asks first and nothing else could
 * answer.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, friendlyError } from "../../api/client";
import { ACTIVITY, label } from "../../lib/labels";
import { formatDateTime, timeAgo } from "../../lib/model";
import { Button, EmptyState, ErrorBanner, SectionTitle, Spinner } from "../../components/ui";

export default function Notes({ caseId }: { caseId: string }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");

  const notes = useQuery({ queryKey: ["notes", caseId], queryFn: () => api.listNotes(caseId) });
  const activity = useQuery({
    queryKey: ["activity", caseId],
    queryFn: () => api.listActivity(caseId),
  });

  const add = useMutation({
    mutationFn: () => api.addNote(caseId, body.trim()),
    onSuccess: async () => {
      setBody("");
      await queryClient.invalidateQueries({ queryKey: ["notes", caseId] });
      await queryClient.invalidateQueries({ queryKey: ["activity", caseId] });
    },
  });

  return (
    <div className="grid gap-10 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <section>
        <h2 className="text-base font-semibold">Notas internas</h2>
        <p className="mt-0.5 text-sm text-muted">
          Para el equipo. No salen en el informe ni las lee el sistema.
        </p>

        <textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Escribí una nota…"
          className="mt-3 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />
        {add.isError ? (
          <div className="mt-2">
            <ErrorBanner {...friendlyError(add.error)} />
          </div>
        ) : null}
        <Button
          variant="primary"
          className="mt-2"
          disabled={!body.trim() || add.isPending}
          onClick={() => add.mutate()}
        >
          {add.isPending ? "Guardando…" : "Agregar nota"}
        </Button>

        {notes.isLoading ? <Spinner /> : null}
        {notes.data?.length === 0 ? (
          <div className="mt-6">
            <EmptyState title="Todavía no hay notas" text="La primera la escribís vos." />
          </div>
        ) : (
          <ul className="mt-6 space-y-3">
            {(notes.data ?? []).map((note) => (
              <li key={note.id} className="rounded-lg border border-line p-3">
                <p className="text-sm whitespace-pre-line">{note.body}</p>
                <p className="mt-2 text-xs text-muted">
                  {note.author_name ?? "—"} · {timeAgo(note.created_at)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <SectionTitle hint="Quién hizo qué, en orden. Es la respuesta a «¿por qué este legajo está así?».">
          Historial
        </SectionTitle>
        {activity.isLoading ? <Spinner /> : null}
        {activity.data?.length === 0 ? (
          <p className="text-sm text-muted">Sin movimientos todavía.</p>
        ) : (
          <ol className="space-y-3 border-l border-line pl-4">
            {(activity.data ?? []).map((item) => (
              <li key={item.id} className="relative">
                <span className="absolute top-1.5 -left-[21px] size-2 rounded-full bg-line" />
                <div className="text-sm">{label(ACTIVITY, item.kind)}</div>
                <div className="text-xs text-muted">
                  {item.user_name ?? "—"} · {formatDateTime(item.created_at)}
                </div>
                {describe(item.detail) ? (
                  <div className="mt-0.5 text-xs text-muted">{describe(item.detail)}</div>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

/** The detail worth showing, in words. Anything else stays out of the timeline. */
function describe(detail: Record<string, unknown>): string | null {
  if (!detail || typeof detail !== "object") return null;
  if (typeof detail.outcome === "string") {
    const agreed = detail.agreed_with_system;
    return agreed === false ? "No coincidió con la sugerencia del sistema" : null;
  }
  if (typeof detail.field === "string") {
    return detail.agrees === false
      ? `Marcó una diferencia en «${detail.field}»`
      : `Verificó «${detail.field}»`;
  }
  if (typeof detail.filename === "string") return String(detail.filename);
  if (typeof detail.assigned_to === "string") return `A cargo de ${detail.assigned_to}`;
  if (detail.assigned_to === null) return "Sin asignar";
  if (typeof detail.legal_name === "string") return `Ahora se llama ${detail.legal_name}`;
  return null;
}
